/**
 * System Health API
 * Wraps /api/v1/{health,live,ready,metrics}/ for the admin status board.
 *
 * Uses a raw axios instance (no shared interceptor) so polling /ready/ when
 * the DB is down does not spam toast errors every 30 seconds.
 */
import axios from 'axios';
import { env, ensureHttps } from '@/config/environment';

const baseURL = ensureHttps(env.apiBaseUrl);

const raw = axios.create({
  baseURL,
  timeout: 6000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export type ProbeStatus = 'ok' | 'degraded' | 'down';

export interface ProbeResult {
  name: string;
  endpoint: string;
  status: ProbeStatus;
  http_status: number | null;
  latency_ms: number | null;
  data: Record<string, unknown> | null;
  error?: string;
}

async function probe(name: string, path: string): Promise<ProbeResult> {
  const start = performance.now();
  try {
    const response = await raw.get(path, { validateStatus: () => true });
    const latency = Math.round(performance.now() - start);
    const ok = response.status >= 200 && response.status < 300;
    const degraded = response.status >= 500 && response.status < 600 && response.status !== 503;
    return {
      name,
      endpoint: path,
      status: ok ? 'ok' : degraded ? 'degraded' : 'down',
      http_status: response.status,
      latency_ms: latency,
      data: typeof response.data === 'object' && response.data !== null
        ? (response.data as Record<string, unknown>)
        : null,
    };
  } catch (err: unknown) {
    const latency = Math.round(performance.now() - start);
    const message = err instanceof Error ? err.message : String(err);
    return {
      name,
      endpoint: path,
      status: 'down',
      http_status: null,
      latency_ms: latency,
      data: null,
      error: message,
    };
  }
}

export const systemAPI = {
  health: () => probe('Application', '/health/'),
  liveness: () => probe('Liveness', '/live/'),
  readiness: () => probe('Readiness', '/ready/'),
  metrics: () => probe('Metrics', '/metrics/'),

  async probeAll(): Promise<ProbeResult[]> {
    return Promise.all([this.health(), this.liveness(), this.readiness(), this.metrics()]);
  },
};

export default systemAPI;
